import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Typography,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { AlertTriangle, CheckCircle2, RotateCw } from 'lucide-react';
import {
  activateSharedAccount,
  dryRunSharedAccount,
  getSharedAccountConfig,
  getSharedFubTeams,
  saveSharedAccountRoutes,
  SharedAccountDryRun,
  SharedCrmRoute,
} from '../../../api/rollout';
import { useOrganizationsData } from '../../../hooks/organizations/useOrganizationsData';

interface SharedFubAccountSetupProps {
  connectionId: string;
  onActivated?: () => void;
}

type RouteSelections = Record<string, string>;

const getErrorMessage = (error: unknown): string => {
  const candidate = error as {
    response?: { data?: { detail?: string | { message?: string } } };
    message?: string;
  };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return candidate.message || 'The request could not be completed.';
};

export const SharedFubAccountSetup: React.FC<SharedFubAccountSetupProps> = ({
  connectionId,
  onActivated,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data: organizations, isLoading: organizationsLoading } = useOrganizationsData();
  const [selections, setSelections] = useState<RouteSelections>({});
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [localDryRun, setLocalDryRun] = useState<SharedAccountDryRun | null>(null);

  const configQuery = useQuery({
    queryKey: ['shared-fub-account', connectionId],
    queryFn: () => getSharedAccountConfig(connectionId),
    enabled: Boolean(connectionId),
  });
  const teamsQuery = useQuery({
    queryKey: ['shared-fub-teams', connectionId],
    queryFn: () => getSharedFubTeams(connectionId),
    enabled: Boolean(connectionId),
  });

  useEffect(() => {
    if (!configQuery.data?.routes) return;
    setSelections(
      Object.fromEntries(
        configQuery.data.routes.map((route) => [
          String(route.provider_scope_id),
          route.organization_id,
        ])
      )
    );
    setLocalDryRun(configQuery.data.dry_run ?? null);
  }, [configQuery.data]);

  const orgNames = useMemo(
    () => new Map(organizations.map((org) => [org.id, org.org_name || org.id])),
    [organizations]
  );
  const selectedOrgIds = Object.values(selections).filter(Boolean);
  const hasDuplicateOrg = new Set(selectedOrgIds).size !== selectedOrgIds.length;
  const routes: SharedCrmRoute[] = Object.entries(selections)
    .filter(([, organizationId]) => Boolean(organizationId))
    .map(([teamId, organizationId]) => ({
      provider_scope_id: teamId,
      organization_id: organizationId,
      enabled: true,
    }));
  const hasUnsavedChanges = Boolean(configQuery.data) && JSON.stringify(
    [...routes]
      .map((route) => [route.provider_scope_id, route.organization_id])
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
  ) !== JSON.stringify(
    [...(configQuery.data?.routes || [])]
      .filter((route) => route.enabled !== false)
      .map((route) => [route.provider_scope_id, route.organization_id])
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
  );

  const saveMutation = useMutation({
    mutationFn: () => saveSharedAccountRoutes(connectionId, routes, replaceExisting),
    onSuccess: async () => {
      setLocalDryRun(null);
      await queryClient.invalidateQueries({ queryKey: ['shared-fub-account', connectionId] });
      showToast({ message: 'Team mappings saved. Run the dry run before activation.', variant: 'success' });
    },
    onError: (error) => showToast({ message: getErrorMessage(error), variant: 'error' }),
  });
  const dryRunMutation = useMutation({
    mutationFn: () => dryRunSharedAccount(connectionId),
    onSuccess: (result) => {
      setLocalDryRun(result);
      queryClient.invalidateQueries({ queryKey: ['shared-fub-account', connectionId] });
      showToast({ message: 'Dry run complete. Review the routing totals below.', variant: 'success' });
    },
    onError: (error) => showToast({ message: getErrorMessage(error), variant: 'error' }),
  });
  const activateMutation = useMutation({
    mutationFn: () => activateSharedAccount(connectionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      await queryClient.invalidateQueries({ queryKey: ['shared-fub-account', connectionId] });
      showToast({ message: 'Shared Follow Up Boss routing is active and initial sync is queued.', variant: 'success' });
      onActivated?.();
    },
    onError: (error) => showToast({ message: getErrorMessage(error), variant: 'error' }),
  });

  if (configQuery.isLoading || teamsQuery.isLoading || organizationsLoading) {
    return (
      <div className="flex items-center gap-2 py-5 text-sm text-gray-600 dark:text-gray-400">
        <RotateCw className="h-4 w-4 animate-spin" /> Loading FUB Teams and organizations…
      </div>
    );
  }
  if (configQuery.error || teamsQuery.error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{getErrorMessage(configQuery.error || teamsQuery.error)}</AlertDescription>
      </Alert>
    );
  }

  const dryRun = localDryRun ?? configQuery.data?.dry_run ?? null;
  const isActive = configQuery.data?.routing_status === 'active';
  const canActivate = Boolean(dryRun) && !hasUnsavedChanges && (dryRun?.ambiguous ?? 0) === 0;

  return (
    <div className="mt-4 border-t border-gray-200 pt-4 dark:border-white/10">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <Typography variant="h5" className="font-semibold">Route FUB Teams</Typography>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Each selected FUB Team becomes the CRM boundary for exactly one Speculo organization.
            Unmapped and ambiguous contacts are excluded.
          </p>
        </div>
        {isActive && (
          <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <CheckCircle2 className="h-3.5 w-3.5" /> Active
          </span>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-white/10">
        <table className="w-full min-w-[620px] text-left text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2 font-medium">FUB Team</th>
              <th className="px-3 py-2 font-medium">Members</th>
              <th className="px-3 py-2 font-medium">Speculo organization</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-white/10">
            {(teamsQuery.data || []).map((team) => (
              <tr key={team.id} className={!team.selectable ? 'opacity-60' : ''}>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-gray-800 dark:text-gray-200">{team.name}</div>
                  {team.blocked_reason && <div className="mt-0.5 text-xs text-amber-600">{team.blocked_reason}</div>}
                </td>
                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{team.userIds.length}</td>
                <td className="px-3 py-2.5">
                  <select
                    aria-label={`Organization for ${team.name}`}
                    value={selections[team.id] || ''}
                    disabled={!team.selectable || isActive}
                    onChange={(event) => {
                      setSelections((current) => ({ ...current, [team.id]: event.target.value }));
                      setLocalDryRun(null);
                    }}
                    className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 disabled:bg-gray-100 dark:border-white/15 dark:bg-gray-900 dark:text-gray-100 dark:disabled:bg-white/5"
                  >
                    <option value="">Not mapped</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{orgNames.get(org.id)}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasDuplicateOrg && (
        <Alert variant="warning" className="mt-3">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Each Speculo organization can be mapped only once.</AlertDescription>
        </Alert>
      )}

      {!isActive && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            <Checkbox checked={replaceExisting} onChange={(event) => setReplaceExisting(event.target.checked)} />
            Replace existing CRM connections for mapped organizations
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!routes.length || hasDuplicateOrg || saveMutation.isPending}
              isLoading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save mappings
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!configQuery.data?.routes?.length || hasUnsavedChanges || dryRunMutation.isPending}
              isLoading={dryRunMutation.isPending}
              onClick={() => dryRunMutation.mutate()}
            >
              Run dry run
            </Button>
            <Button
              size="sm"
              disabled={!canActivate || activateMutation.isPending}
              isLoading={activateMutation.isPending}
              onClick={() => activateMutation.mutate()}
            >
              Activate routing
            </Button>
          </div>
        </div>
      )}

      {dryRun && (
        <div className="mt-4 rounded-lg bg-gray-50 p-3 dark:bg-white/5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ['Examined', dryRun.contacts_examined],
              ['Routed', dryRun.routed],
              ['Unmapped', dryRun.unmapped],
              ['Ambiguous', dryRun.ambiguous],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">{value}</div>
              </div>
            ))}
          </div>
          {Object.keys(dryRun.by_organization || {}).length > 0 && (
            <div className="mt-3 border-t border-gray-200 pt-2 text-xs dark:border-white/10">
              {Object.entries(dryRun.by_organization).map(([orgId, count]) => (
                <div key={orgId} className="flex justify-between py-1 text-gray-600 dark:text-gray-400">
                  <span>{orgNames.get(orgId) || orgId}</span><span>{count} contacts</span>
                </div>
              ))}
            </div>
          )}
          {dryRun.truncated && <p className="mt-2 text-xs text-amber-600">Preview reached the 20,000-contact limit.</p>}
        </div>
      )}
    </div>
  );
};
